import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import { useModal } from "../../../contexts/ModalContext";
import ModalNovoTipo from "./ModalNovoTipo";
import ModalEditarTipo from "./ModalEditarTipo";
import ModalDeleteTipo from "./ModalDeleteTipo";
import useApi from "../../../hooks/useApi";

const Container = styled.div`
    width: 100%;
    height: min(70dvh, 520px);
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    flex-shrink: 0;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: #263b4b;
        flex-shrink: 0;
    }
`;

const BotaoNovo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: #263b4b;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;

    &:hover {
        background-color: #1e2f3c;
        transform: translateY(-1px);
    }
`;

const TableShell = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const ModalCatalogoTipoRisco = ({ categoria, fecharModal, updateCategorias }) => {
    const api = useApi();
    const { isModalOpen, openModal, closeModal } = useModal();

    const [loading, setLoading] = useState(true);
    const [tiposRisco, setTiposRisco] = useState([]);
    const [selectedTipo, setSelectedTipo] = useState(null);

    const getData = useCallback(async () => {
        if (!categoria?.uuid) return;

        setLoading(true);

        try {
            const { data } = await api.post("/Painel/API/Desvios/TiposRiscosCat/", {
                categoria: categoria.uuid,
            });

            setTiposRisco(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setTiposRisco([]);
        } finally {
            setLoading(false);
        }
    }, [categoria?.uuid]);

    useEffect(() => {
        getData();
    }, [getData]);

    const openEditTipoRisco = useCallback(
        (tipo) => {
            setSelectedTipo(tipo);
            openModal("ModalEditTipoRisco");
        },
        [openModal]
    );

    const openDeleteTipoRisco = useCallback(
        (tipo) => {
            setSelectedTipo(tipo);
            openModal("ModalDeleteTipoRisco");
        },
        [openModal]
    );

    const closeDeleteTipoRisco = useCallback(() => {
        setSelectedTipo(null);
        closeModal();
    }, [closeModal]);

    const columns = useMemo(
        () => [
            {
                id: "tipo",
                header: "TIPO DE RISCO",
                accessor: "tipo",
                sortable: true,
            },
            {
                id: "classificacao",
                header: "CLASSIFICAÇÃO",
                accessor: (row) => row.classificacao_desc || "-",
                sortable: true,
                sortValue: (row) => row.classificacao_desc,
            },
            {
                id: "acoes",
                header: "AÇÕES",
                sortable: false,
                width: "90px",
                align: "center",
                render: (_, row) => (
                    <AcoesCelula>
                        <BotaoAcao
                            type="button"
                            title="Editar tipo de risco"
                            aria-label="Editar tipo de risco"
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditTipoRisco(row);
                            }}
                        >
                            <FiEdit2 size={14} />
                        </BotaoAcao>
                        <BotaoAcao
                            $danger
                            type="button"
                            title="Remover tipo de risco"
                            aria-label="Remover tipo de risco"
                            onClick={(e) => {
                                e.stopPropagation();
                                openDeleteTipoRisco(row);
                            }}
                        >
                            <FiTrash2 size={14} />
                        </BotaoAcao>
                    </AcoesCelula>
                ),
            },
        ],
        [openEditTipoRisco, openDeleteTipoRisco]
    );

    return (
        <>
            <Overlay state={true}>
                <Modal size="lg" header close={fecharModal} blockOverflow>
                    <Container>
                        <Header>
                            <Titulo>
                                <FiAlertTriangle size={20} />
                                CATÁLOGO TIPOS DE RISCO — {categoria?.nome}
                            </Titulo>
                            <BotaoNovo type="button" onClick={() => openModal("ModalNovoTipoRisco")}>
                                <FiPlus size={16} />
                                ADICIONAR
                            </BotaoNovo>
                        </Header>

                        <TableShell>
                            <SortableTable
                                columns={columns}
                                data={tiposRisco}
                                isLoading={loading}
                                loadingText="Carregando tipos de risco..."
                                emptyText="NENHUM TIPO DE RISCO CADASTRADO"
                                initialSort={{ key: "tipo", direction: "asc" }}
                                getRowId={(row) => row.uuid}
                            />
                        </TableShell>
                    </Container>
                </Modal>
                {isModalOpen("ModalNovoTipoRisco") && (
                    <ModalNovoTipo
                        categoria={categoria}
                        updateTipos={getData}
                        updateCategorias={updateCategorias}
                    />
                )}
                {isModalOpen("ModalEditTipoRisco") && selectedTipo && (
                    <ModalEditarTipo
                        tipo={selectedTipo}
                        updateTipos={getData}
                        updateCategorias={updateCategorias}
                    />
                )}
                {isModalOpen("ModalDeleteTipoRisco") && (
                    <ModalDeleteTipo
                        tipo={selectedTipo}
                        fecharModal={closeDeleteTipoRisco}
                        updateData={getData}
                        updateCategorias={updateCategorias}
                    />
                )}
            </Overlay>
        </>
    );
};

export default ModalCatalogoTipoRisco;
